import { motion } from "framer-motion";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
    {
        q: "Is Las Vegas Restroom Locator available on the App Store?",
        a: "We're putting the finishing touches on the listing right now. The moment it goes live, the download buttons on this page will link straight to the App Store — check back soon.",
    },
    {
        q: "Is the app free?",
        a: "Yes. The core map — every mapped restroom, access notes, and filters — is free. We may add optional premium extras later, but finding a restroom in an emergency will never cost you anything.",
    },
    {
        q: "How do I report a closed or inaccurate restroom?",
        a: "Use the support form below and pick 'Data Correction' as the category. Tell us the property and what changed (closed for renovation, moved floors, now keycard-only) and we'll update the map in the next data push.",
    },
    {
        q: "Do you track my location?",
        a: "No. Your GPS position is used on-device only, to sort restrooms by walking distance. We never store, upload, or sell your location history.",
    },
    {
        q: "Does the app work offline?",
        a: "Yes. The full Strip and Downtown map — including pins, access notes, and filters — is cached on your phone, so it keeps working in casino dead zones and basements.",
    },
    {
        q: "How do I suggest a new location or share an access code?",
        a: "Send it through the support form with the 'Feature Request' or 'Data Correction' category. Verified submissions are added to the map and credited in the app's release notes.",
    },
];

export default function Faq() {
    return (
        <section id="faq" className="relative border-t border-white/5 bg-[#0D0E15]/40 py-28 sm:py-36" data-testid="faq-section">
            <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.6fr]">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-neon-cyan">
                        02 — Answers
                    </p>
                    <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                        FREQUENTLY
                        <br />
                        ASKED <span className="text-neon-gold neon-gold">QUESTIONS</span>
                    </h2>
                    <p className="mt-6 max-w-sm text-base leading-relaxed text-slate-400">
                        Everything players, tourists, and high-rollers ask us most. Don't see
                        your question? The support form below lands straight in our inbox.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
                >
                    <Accordion type="single" collapsible className="space-y-3">
                        {FAQS.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`item-${i}`}
                                data-testid={`faq-accordion-item-${i + 1}`}
                                className="glass-panel overflow-hidden rounded-xl border-white/10 px-5 transition-colors duration-300 data-[state=open]:border-neon-cyan/40"
                            >
                                <AccordionTrigger
                                    className="py-5 text-left text-base font-semibold text-white hover:text-neon-cyan hover:no-underline [&[data-state=open]>svg]:text-neon-cyan"
                                    data-testid={`faq-accordion-trigger-${i + 1}`}
                                >
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent
                                    className="pb-5 text-sm leading-relaxed text-slate-400"
                                    data-testid={`faq-accordion-content-${i + 1}`}
                                >
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </motion.div>
            </div>
        </section>
    );
}
